import {
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    DrawerFooter,
    Flex,
    HStack,
    Text,
    CloseButton,
    Box,
    type DrawerProps,
} from '@chakra-ui/react';
import type { ComponentProps, ReactNode, Ref } from 'react';
import { useForceSidebarCollapsed } from '../contexts/SidebarCollapseOverrideContext';

type DrawerBodyProps = ComponentProps<typeof DrawerBody>;
type DrawerFooterProps = ComponentProps<typeof DrawerFooter>;
type DrawerContentProps = ComponentProps<typeof DrawerContent>;

export interface AppDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    title: ReactNode;
    /** Optional subtitle under the title (grows header past the top bar). */
    subtitle?: ReactNode;
    /** Optional leading icon/content next to the title */
    titleAddon?: ReactNode;
    size?: DrawerProps['size'];
    children: ReactNode;
    /** When set, renders a footer. Omit for view-only drawers. */
    footer?: ReactNode;
    /** Optional left-side footer content (e.g. remaining required count). */
    footerLeft?: ReactNode;
    bodyProps?: DrawerBodyProps;
    footerProps?: DrawerFooterProps;
    /** Extra content on the right side of the header (before the close button). */
    headerRight?: ReactNode;
    /** Content between the top-bar-height header and the body (e.g. search strip). */
    headerBelow?: ReactNode;
    /** Non-action bottom chrome (e.g. pagination). */
    bottomBar?: ReactNode;
    closeOnOverlayClick?: boolean;
    blockScrollOnMount?: boolean;
    trapFocus?: boolean;
    /** When true, omit DrawerOverlay (page stays interactive). */
    hideOverlay?: boolean;
    contentRef?: Ref<HTMLDivElement>;
    contentProps?: DrawerContentProps;
}

/**
 * Shared right-drawer chrome: header matches TopBar (white + gray.200 hairline),
 * left edge uses the same light border as the Order Detail RHS panel.
 * Do not use for OrderDrawer — that surface keeps its own layout.
 */
const AppDrawer = ({
    isOpen,
    onClose,
    title,
    subtitle,
    titleAddon,
    size = 'md',
    children,
    footer,
    footerLeft,
    bodyProps,
    footerProps,
    headerRight,
    headerBelow,
    bottomBar,
    closeOnOverlayClick,
    blockScrollOnMount,
    trapFocus,
    hideOverlay = false,
    contentRef,
    contentProps,
}: AppDrawerProps) => {
    // ENG-2180: any open drawer collapses the sidebar to maximize content width.
    useForceSidebarCollapsed(isOpen);
    const hasSubtitle = subtitle != null && subtitle !== false && subtitle !== '';

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            placement="right"
            size={size}
            closeOnOverlayClick={closeOnOverlayClick ?? !hideOverlay}
            blockScrollOnMount={blockScrollOnMount ?? !hideOverlay}
            trapFocus={trapFocus ?? !hideOverlay}
        >
            {!hideOverlay && <DrawerOverlay />}
            <DrawerContent
                ref={contentRef}
                data-partbay-drawer=""
                bg="white"
                borderLeftWidth="1px"
                borderLeftColor="border.subtle"
                {...contentProps}
            >
                <Flex
                    as="header"
                    h={hasSubtitle ? 'auto' : 'topbar'}
                    minH="topbar"
                    align="center"
                    justify="space-between"
                    borderBottom="1px solid"
                    borderColor="border.subtle"
                    bg="white"
                    px={6}
                    py={hasSubtitle ? 4 : 0}
                    flexShrink={0}
                    gap={3}
                >
                    <HStack spacing={2} minW={0} align={hasSubtitle ? 'flex-start' : 'center'}>
                        {titleAddon}
                        <Flex direction="column" minW={0}>
                            {typeof title === 'string' ? (
                                <Text fontSize="lg" fontWeight="bold" color="gray.800" noOfLines={1}>
                                    {title}
                                </Text>
                            ) : (
                                title
                            )}
                            {hasSubtitle &&
                                (typeof subtitle === 'string' ? (
                                    <Text fontSize="sm" color="gray.500" noOfLines={1} mt={0.5}>
                                        {subtitle}
                                    </Text>
                                ) : (
                                    subtitle
                                ))}
                        </Flex>
                    </HStack>
                    <HStack spacing={2} flexShrink={0}>
                        {headerRight}
                        <CloseButton onClick={onClose} size="sm" />
                    </HStack>
                </Flex>

                {headerBelow}

                <DrawerBody pt={6} px={6} overflowY="auto" bg="white" {...bodyProps}>
                    {children}
                </DrawerBody>

                {bottomBar}

                {footer != null && (
                    <DrawerFooter
                        px={6}
                        py={4}
                        {...footerProps}
                        borderTopWidth="1px"
                        borderColor="border.default"
                        bg="white"
                    >
                        <HStack
                            spacing={3}
                            w="100%"
                            justify={footerLeft != null ? 'space-between' : 'flex-end'}
                            align="center"
                        >
                            {footerLeft != null && (
                                <Box as="span" flexShrink={0}>
                                    {footerLeft}
                                </Box>
                            )}
                            <HStack spacing={3} flexShrink={0}>
                                {footer}
                            </HStack>
                        </HStack>
                    </DrawerFooter>
                )}
            </DrawerContent>
        </Drawer>
    );
};

export default AppDrawer;
